import {
    useMemo, useState,
} from 'react'

import useFieldsWithClassName from 'app/hooks/useFieldsWithClassName'
import DomainObjectForm from 'app/shared-components/DomainObjectForm'
import Button from 'app/shared-components/Button'
import ButtonContainer from 'app/shared-components/ButtonContainer'
import useEnumValues from 'app/hooks/useEnumValues'
import StatusHandler from 'app/shared-components/StatusHandler'
import {
    NOT_ALLOWED_POSTPONE_ORDER_STATUSES,
} from 'app/Apps/OrderManagement/Orders/utils/orderStatus'
import {
    CustomerOrderSource, OrderStatuses,
} from 'app/enums/label.enums'
import {
    CancelOrderData, Order,
} from 'app/Apps/OrderManagement/Orders/orders.types'
import useCancelOrder from 'app/Apps/OrderManagement/Orders/hooks/useCancelOrder'
import {
    generateSkyNetLink, RoutesConfig,
} from 'app/SkyNetRoutes'
import usePricingRoutes from 'app/SkyNetRoutes/hooks/usePricingRoutes'
import useOrderManagementRoutes from 'app/SkyNetRoutes/hooks/useOrderManagementRoutes'

import CancellationOrderDialog from '../../Components/CancellationOrderDialog'
import useGetOrderByNumber from '../../hooks/useGetOrderByNumber'
import OrderComment from '../../Components/OrderComment'

import getFields from './customerTransportsOverview.fields'
import useStyles from './CustomerTransportsOverview.style'
import PostponeDialog from './PostponeDialog'

const DOMAIN_NAME = 'OrderByNumberView'

const CustomerTransportsOverview = ({
    id: orderNumber,
}: { id: string }) => {
    const {
        Lanes: LanesRoutes,
    } = useOrderManagementRoutes()
    const {
        ContractBasis: ContractBasisRoutes,
        LaneGroups: LaneGroupsRoutes,
    } = usePricingRoutes()
    const {
        data = {} as Order,
        isDataReady,
        isError,
        error,
        isFetching,
        invalidate,
    } = useGetOrderByNumber(orderNumber)

    const {
        orderStatus,
        leaseType,
        lane,
        billing,
        source,
    } = data

    const {
        classes,
    } = useStyles()
    const getAllowedValues = useEnumValues()

    const isShowPostpone: boolean = useMemo(() => {
        return !NOT_ALLOWED_POSTPONE_ORDER_STATUSES.includes(orderStatus)
    }, [orderStatus])

    const isCancelled: boolean = useMemo(() => {
        return orderStatus === OrderStatuses.CANCELLED
    }, [orderStatus])

    const {
        isShowCancel,
        openCancellation,
        setCancellationOpen,
    }: CancelOrderData = useCancelOrder({
        orderStatus,
    })

    const fields = useFieldsWithClassName(
        getFields(
            {
                orderStatus,
                isCancelled,
                leaseType,
                laneLink: lane?.id ? generateSkyNetLink({
                    domainPath: (LanesRoutes.OperationalLanes as RoutesConfig)
                        .LaneImplementation.path,
                    params: {
                        key: lane?.id,
                    },
                }) : undefined,
                laneFeesLink: billing?.laneFees?.id ? generateSkyNetLink({
                    domainPath: LaneGroupsRoutes.LaneFees.path,
                    params: {
                        key: lane?.laneGroup?.id,
                        feesId: billing?.laneFees?.id,
                    },
                }) : undefined,
                additionalFeesLink: billing?.additionalFees?.id ? generateSkyNetLink({
                    domainPath: ContractBasisRoutes.AdditionalFees.path,
                    params: {
                        key: lane?.laneGroup?.contractBasisId,
                        id: billing?.additionalFees?.id,
                    },
                }) : undefined,
                getAllowedValues,
                laneNumber: lane?.laneNumber,
                laneFeesNumber: billing?.laneFees?.laneFeesNumber,
                additionalFeesNumber: billing?.additionalFees?.additionalFeesNumber,
                isBookingSource: source === CustomerOrderSource.BOOKING_PORTAL,
            },
        ),
        classes,
    )

    const [
        openPostpone,
        setPostponeOpen,
    ] = useState<boolean>(false)

    return (
        <StatusHandler
            isSuccess={isDataReady}
            isFetching={isFetching}
            isError={isError}
            error={error}
        >
            <>
                <DomainObjectForm
                    value={data}
                    className={isCancelled ? classes.gridWrapperCancellation : classes.gridWrapper}
                    fields={fields}
                    disabled
                    name={DOMAIN_NAME}
                />
                <ButtonContainer>
                    {isShowCancel && (
                        <Button
                            secondary
                            label="cancel order"
                            onClick={() => {
                                setCancellationOpen(true)
                            }}
                        />
                    )}
                    {isShowPostpone && (
                        <Button
                            data-testid="postpone-order-btn"
                            secondary
                            label="postpone order"
                            onClick={() => {
                                setPostponeOpen(true)
                            }}
                        />
                    )}
                </ButtonContainer>
                <OrderComment orderNumber={orderNumber} />
                <CancellationOrderDialog
                    orderNumber={orderNumber}
                    onSuccess={invalidate}
                    open={openCancellation}
                    setOpen={setCancellationOpen}
                />
                <PostponeDialog
                    open={openPostpone}
                    setOpen={setPostponeOpen}
                    onSuccess={invalidate}
                    order={data}
                />
            </>
        </StatusHandler>
    )
}

export default CustomerTransportsOverview
